import { useState } from "react";
import { Button, App } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";

interface ExecPdfExportButtonProps {
  /** A ref or callback that returns the DOM node to capture. */
  getNode: () => HTMLElement | null;
  filename?: string;
}

async function exportNodeAsPdf(node: HTMLElement, filename: string) {
  const [jsPdfMod, html2canvasMod] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const canvas = await html2canvasMod.default(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
  });
  const pdf = new jsPdfMod.default({ unit: "pt", format: "letter" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let position = 0;
  let heightLeft = imgHeight;
  const dataUrl = canvas.toDataURL("image/png");
  pdf.addImage(dataUrl, "PNG", 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(dataUrl, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  pdf.save(filename);
}

export default function ExecPdfExportButton({ getNode, filename }: ExecPdfExportButtonProps) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    const node = getNode();
    if (!node) {
      message.error("Could not find dashboard content to export.");
      return;
    }
    setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await exportNodeAsPdf(node, filename || `OAIP-exec-dashboard-${today}.pdf`);
    } catch (err) {
      console.error("Failed to export exec dashboard PDF:", err);
      message.error("PDF export failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      type="primary"
      icon={<FilePdfOutlined />}
      loading={busy}
      onClick={handleClick}
      aria-label="Export executive dashboard as PDF"
    >
      Export PDF
    </Button>
  );
}
